import clsx from 'clsx';
import { forwardRef, useMemo } from 'react';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
} from '@dynatrace/strato-components/core';
import { _useContainerColoring as useContainerColoring } from '@dynatrace/strato-components/layouts';

import {
  keyboardShortcutKeyCSS,
  keyboardShortcutKeyWrapperCSS,
} from './KeyboardShortcut.sty.js';

// #region Typings
/**
 * Accepted properties for KeyboardShortcut
 * @public
 */
export interface KeyboardShortcutProps
  extends StylingProps,
    DataTestId,
    MaskingProps {
  /**
   * The list of keys the component should display, specified in the aria-keyshortcuts format.
   */
  keys: string;
  /**
   * The variant that should be used for styling the keys.
   * @defaultValue 'default'
   */
  variant?: 'default' | 'minimal';
  /**
   * Whether the component should be rendered in a disabled state.
   * @defaultValue 'false'
   */
  disabled?: boolean;
}
// #endregion Typings

const keyMappings: Record<string, string> = {
  meta: 'Cmd',
  control: 'Ctrl',
  escape: 'Esc',
  altgraph: 'Option',
  delete: 'Del',
  arrowup: '↑',
  arrowdown: '↓',
  arrowright: '→',
  arrowleft: '←',
  plus: '+',
  minus: '-',
  multiply: '*',
  divide: '/',
};

/** @internal */
export function createKeyArrayFromString(keys: string): string[] {
  return keys
    .split(' ')[0]
    .split('+')
    .filter((key) => key !== '')
    .map(
      (key) =>
        keyMappings[key.toLowerCase()] ??
        `${key.charAt(0).toUpperCase()}${key.slice(1)}`,
    );
}

/**
 * The `KeyboardShortcut` component visualizes one or multiple keys to communicate keyboard shortcuts for certain actions.
 * To specify a shortcut, provide the key sequence in the {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-keyshortcuts | aria-keyshortcuts} format.
 * @public
 */
export const KeyboardShortcut = forwardRef<HTMLElement, KeyboardShortcutProps>(
  (props, forwardedRef) => {
    const {
      keys,
      variant = 'default',
      disabled = false,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      className: consumerClassName,
      style: consumerStyle,
      ...remainingProps
    } = props;

    const keyArray = useMemo(() => createKeyArrayFromString(keys), [keys]);

    const containerColoring = useContainerColoring();
    const onAccent = containerColoring.variant === 'accent';

    return (
      <kbd
        ref={forwardedRef}
        aria-disabled={disabled}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        className={clsx(keyboardShortcutKeyWrapperCSS, consumerClassName)}
        style={consumerStyle}
        {...remainingProps}
      >
        {keyArray.map((key: string, index: number) => (
          <kbd
            key={`${key}-${index}`}
            className={keyboardShortcutKeyCSS({ variant, onAccent, disabled })}
          >
            {key}
          </kbd>
        ))}
      </kbd>
    );
  },
);
